import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor,UntypedFormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { AppService, CoreProxyService, CoreTypes, FormValidatorService, LabelValue } from 'common-cloudstudio';
import { Subscription } from 'rxjs';
import { IncidentService } from '../../services/incident.service';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-incident-location-ref',
  templateUrl: './incident-location-ref.component.html',
  styleUrls: ['./incident-location-ref.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IncidentLocationRefComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IncidentLocationRefComponent),
      multi: true,
    }
  ],
})
export class IncidentLocationRefComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @ViewChild('search') public searchElementRef: ElementRef;
  _data: CoreTypes.SimpleIncident = new CoreTypes.SimpleIncident();
  @Input('data')
  set data(data: CoreTypes.SimpleIncident) {
    this.writeValue(data);
  }
  get data() {
    return this._data;
  }
  @Input() kilometerPoint: number = 0;
  @Input() readonly: boolean = false;
  @Input() incidentID: number = 0;
  @Input() displayTitle: boolean = false;

  originalValue:  CoreTypes.SimpleIncident = new CoreTypes.SimpleIncident();
  isDisabled: boolean;
  onChange = (_:any) => { }
  onTouch = () => { }

  @Input() displayInputLocation: boolean = true;
  @Input() displayOnModal: boolean = false;
  @Input() vialAxesFullList: CoreTypes.ITS_VialAxes[];
  @Input() roadwayFullList: CoreTypes.ITS_Roadways[] = [];
  @Input() trafficDirectionsFullList: CoreTypes.ITS_TrafficDirection[] = [];
  @Input() sectorFullList: CoreTypes.ITS_Sectors[];
  @Input() subSectorFullList: CoreTypes.ITS_SubSectors[];

  @Output() onFocusAutocomplete = new EventEmitter<any>();
  @Output() onReferencePoints = new EventEmitter<any>();
  @Output() onClearInputText = new EventEmitter<boolean>();
  @Output() onOpenLocationMap = new EventEmitter<boolean>();

  subscriptions: Subscription[] = [];

  _vialAxesList: LabelValue<number>[] = [];
  _subSectorList: LabelValue<number>[] = [];
  _sectorList: LabelValue<number>[] = [];
  _roadwayList: LabelValue<number>[] = [];
  _trafficDirectionsList: LabelValue<number>[] = [];
  debounceAction: any;
  _loadReference: number = 0;
  isWriting: boolean = false;

  form = this.fb.group({
    vialAxes: ['', []],
    kilometer: [null, []],
    roadway: ['', []],
    trafficDirections: ['', []],
    subSector: ['', []],
    sector: ['', []],
  });
  constructor(
    private fb: UntypedFormBuilder,
    public appService: AppService,
    public _formValidatorService: FormValidatorService,
    private incidentService: IncidentService,
    private _coreProxyService: CoreProxyService,
    private cdRef: ChangeDetectorRef,

  ) { }

  registerOnChange(fn: any): void {
    this.onChange = () => {
      const formValue = this.form.getRawValue();
      const updatedValue = { ...this.originalValue, ...this._data };
  
      if (JSON.stringify(updatedValue) !== JSON.stringify(this.originalValue)) {
        this.originalValue = updatedValue;
        fn(updatedValue);
      }
    };
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if(this.isDisabled) this.form.disable();
  }

  public validate() {
    let errors: ValidationErrors = {};
    Object.keys(this.form.controls).forEach(key => {
      const controlErrors: ValidationErrors = this.form.get(key).errors;
      if (controlErrors != null) {
        errors[key] = controlErrors;
      }
    });
    return errors;
  }

  ngOnInit(): void {
    this.initData();
    this.subscriptions.push(this.vialAxes.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.VialAxisID = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.roadway.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.RoadwayID = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.trafficDirections.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.TrafficDirectionID = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.kilometer.valueChanges.pipe(debounceTime(300)).subscribe(value => {
    if(value >= 0) {
      this._data.KilometerPoint = value;
      this.onInput();
    }
    }));
    this.subscriptions.push(this.sector.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.SectorID = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.subSector.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if(value?.value || value?.value == 0) {
        this._data.SubSectorID = value.value;
        this.onInput();
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnChanges(data: SimpleChanges): void {
    if(data.sectorFullList?.currentValue) {
      this.sectorFullList = data.sectorFullList?.currentValue;
      this.getSectorList();
    }
    if(data?.subSectorFullList?.currentValue) {
      this.subSectorFullList = data.subSectorFullList.currentValue;
      this.getSubSectorList();
    }
    this.initData();
    if(this.readonly) {
      this.setDisabledState(true);
    }
  }

  initData() {
    this.getITSRoadways();
    this.onSelectTrafficDirection();

  }
  
  onInput() {
    this.onTouch();
    this.onChange(this._data);
  }

  writeValue(value: any): void {
    if (value) { 
      this.originalValue = JSON.parse(JSON.stringify(value));
      this.form.patchValue(this.originalValue, { emitEvent: false });
      this._data = value;
      if(!this.isWriting) this.kilometer.setValue(this._data.KilometerPoint);
      const vialAxes = this._data.VialAxisID ? this._vialAxesList.find(x => x.value === this._data.VialAxisID) : this._vialAxesList[0];
      this.vialAxes.setValue(vialAxes);
      const roadway = this._data.RoadwayID ? this._roadwayList.find(x => x.value === this._data.RoadwayID) : this._roadwayList[0];
      this.roadway.setValue(roadway);
      const trafficDirection = this._data.TrafficDirectionID ? this._trafficDirectionsList.find(x => x.value === this._data.TrafficDirectionID) : this._trafficDirectionsList[0];
      this.trafficDirections.setValue(trafficDirection);
      const sector = this._data.SectorID ? this._sectorList.find(x => x.value === this._data.SectorID) : this._sectorList[0];
      this.sector.setValue(sector);
      const subSector = this._data.SubSectorID ? this._subSectorList.find(x => x.value === this._data.SubSectorID) : this._subSectorList[0];
      this.subSector.setValue(subSector);
    } else {
      this._data = new CoreTypes.SimpleIncident();
      this.kilometer.setValue(null);
    }
    this.onInput();
    this.disabledData();
    if(this.readonly) this.setDisabledState(true);
  }

  disabledData() {
    if(this._data?.DateTimeClosed?.Date?.Day) {
      this.vialAxes.disable();
      this.roadway.disable();
      this.subSector.disable();
      this.sector.disable();
    }
    if(this._data?.DateTimeClosed?.Date?.Day || this.incidentID) {
      this.kilometer.disable();
    }
  }

  onSelectRoadway() {
    this.incidentService._roadway = this.roadway.value;
  }

  getITSRoadways() {
    this._roadwayList = [];
    if(!this.roadwayFullList?.length) return; 
    let items: LabelValue<number>[] = [];
    this.roadwayFullList?.forEach((i) => {
      const item = {
        label: i.RoadwayName,
        value: i.RoadwayID,
      };
      items.push(item);
    });
    this._roadwayList = items;
    let roadway =  this._roadwayList.find(axes => axes.value === this._data?.RoadwayID) ?? this._roadwayList[0];
    this.roadway.setValue(roadway);
    this.incidentService._roadway = this.roadway.value;
    this.onSelectVialAxes();
 
  }

  async onSelectVialAxes() {
    if(!this.vialAxesFullList?.length) return;
    let items: LabelValue<number>[] = [];
    this.vialAxesFullList?.forEach((i) => {
      const item = {
        label: i.VialAxisName,
        value: i.VialAxisID,
      };
      items.push(item);
    });
    this._vialAxesList = items;
    let vialAxes =  this._vialAxesList.find(axes => axes.value === this._data?.VialAxisID) ?? this._vialAxesList[0];
    this.vialAxes.setValue(vialAxes);
    setTimeout(() => this.incidentService._vialAxes = this.vialAxes?.value, 10);
    this.onInput();
  }

  async onSelectTrafficDirection() {
    if(!this.trafficDirectionsFullList?.length) return;
    let items: LabelValue<number>[] = [];
    this.trafficDirectionsFullList?.forEach(x => {
      const item = {
        label: x.DirectionName,
        value: x.DirectionID,
      };
      items.push(item);
    })
    this._trafficDirectionsList = items;
    let trafficDirections =  this._trafficDirectionsList?.find(axes => axes.value === this._data?.TrafficDirectionID) ?? this._trafficDirectionsList[0];
    this.trafficDirections.setValue(trafficDirections);
    this.onInput();
  }

  getSectorList() {
    if(!this.sectorFullList?.length) return;
    let items: LabelValue<number>[] = [];
    this._sectorList = [];
    this.sectorFullList?.forEach(x => {
      const item = {
        label: x.SectorName,
        value: x.SectorID,
      };
      items.push(item);
    })
    this._sectorList = items;
    let sector =  this._sectorList?.find(axes => axes.value === this._data?.SectorID) ?? this._sectorList[0];
    this.sector.setValue(sector);
    this.getSubSectorList();
    this.onInput();
  }
  
  getSubSectorList() {
    if(!this.subSectorFullList?.length) return;
    this._subSectorList = [];
    this.subSector.setValue(null);
    this.cdRef.detectChanges();
    let items: LabelValue<number>[] = [];

    this.subSectorFullList?.forEach(x => {
      const item = {
        label: x.SubSectorName,
        value: x.SubSectorID,
      };
      items.push(item);
    })

    this._subSectorList = items;
    let subSector = this._subSectorList?.find(axes => axes.value === this._data?.SubSectorID) ?? this._subSectorList[0];
    this.subSector.setValue(subSector);
    this.cdRef.detectChanges();
    this.onInput();
  }

  onSectorChange(sectorID) {    
    if(!sectorID) return;
    this.subSectorFullList = [];
    this.cdRef.detectChanges();
    this._coreProxyService.IncidentService.EnumITSSubSectorsBySectorID(
      this.appService.getAccessToken(),
      this.appService.getFacilityID(),
      sectorID,
      (data:CoreTypes.ITS_SubSectors[]) => {
        this.subSectorFullList = data;
        this.cdRef.detectChanges();
        setTimeout(() => this.getSubSectorList(), 100); 
      },
      err => {
        this.appService.showException(err);
      }
    );
  }

  onClickclearText() {
    if(this.searchElementRef.nativeElement){
      this.searchElementRef.nativeElement.value = "";
      this.searchElementRef?.nativeElement?.focus();
    }
    this.onClearInputText.emit(true);
  }

  openLocationMap() {
    this.onOpenLocationMap.emit(true);
  }

  searchReferencePoints() {
    this.isWriting = true;
    this._loadReference = 1;
    
    clearTimeout(this.debounceAction);
    this.debounceAction = setTimeout(() => {
      if (!this.kilometer.value) {
        this.isWriting = false;
        return;
      }
      this._coreProxyService.IncidentService.SearchITSVialAxisReferencePoints(
        this.appService.getAccessToken(),
        this.appService.getClientID(),
        this.appService.getFacilityID(),
        this.vialAxes.value.value,
        +this.kilometer.value,
        (data: CoreTypes.ITS_ReferencePoints[])=> {
          if(data.length) {
            const referencePoint = data.find(x => x.KilometerPoint === +this.kilometer.value) ?? data[0];
            this.onReferencePoints.emit(referencePoint);
            const roadway = referencePoint.RoadwayID ? this._roadwayList.find(x => x.value === referencePoint.RoadwayID) ?? { label: referencePoint?.RoadwayDisplay, value: referencePoint.RoadwayID } : this._roadwayList[0];
            this.roadway.setValue(roadway);
            const trafficDirection = referencePoint.DirectionID ? this._trafficDirectionsList.find(x => x.value === referencePoint.DirectionID) ?? { label: referencePoint?.Direction?.DirectionName, value: referencePoint.DirectionID } : this._trafficDirectionsList[0];
            this.trafficDirections.setValue(trafficDirection);
          }
          this.isWriting = false;
          this._loadReference = 0;
        },
        err => {
          this._loadReference = 2;
          this.isWriting = false; 
          this.appService.showException(err);
        }
      );
    }, 500)
  }

  get vialAxes() { return this.form.get('vialAxes'); }
  get kilometer() { return this.form.get('kilometer'); }
  get roadway() { return this.form.get('roadway'); }
  get trafficDirections() { return this.form.get('trafficDirections'); }
  get subSector() { return this.form.get('subSector'); }
  get sector() { return this.form.get('sector'); }
}
